import { useEffect, useState, useCallback, useRef } from "react";
import { Drawer, Form, Input, Button, Space, Spin } from "antd";
import { SaveOutlined } from "@ant-design/icons";
import apiHelpers from "./data";
import { triggerNotification } from "../../common/notify";

const emptyForm = () => ({ tag: "" });

const TagFormDrawer = ({ open, mode, tagId, onClose, onSaved }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formDirty, setFormDirty] = useState(false);
  const initialValuesRef = useRef(null);

  const isCreate = mode === "create";
  const title = isCreate ? "Add Tag" : "Edit Tag";

  const checkDirty = useCallback(() => {
    const current = JSON.stringify(form.getFieldsValue(true));
    setFormDirty(current !== initialValuesRef.current);
  }, [form]);

  useEffect(() => {
    if (!open) return;

    if (isCreate || !tagId) {
      form.resetFields();
      form.setFieldsValue(emptyForm());
      initialValuesRef.current = JSON.stringify(form.getFieldsValue(true));
      setFormDirty(false);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await apiHelpers.fetchTagById(tagId);
        if (cancelled) return;
        form.resetFields();
        form.setFieldsValue({ tag: data?.tag || "" });
        initialValuesRef.current = JSON.stringify(form.getFieldsValue(true));
        setFormDirty(false);
      } catch (err) {
        const mapped = apiHelpers.mapError(err);
        triggerNotification({
          type: "error",
          title: "Failed to load tag",
          message: mapped.message,
        });
        onClose?.();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tagId, mode]);

  const handleSave = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    const payload = { tag: String(values.tag || "").trim() };

    setSaving(true);
    try {
      let saved;
      if (isCreate) {
        saved = await apiHelpers.createTag(payload);
      } else {
        saved = await apiHelpers.updateTag(tagId, payload);
      }
      triggerNotification({
        type: "success",
        title: isCreate ? "Tag created" : "Tag updated",
        message: saved?.tag || payload.tag || "Saved successfully.",
      });
      onSaved?.(saved);
    } catch (err) {
      const mapped = apiHelpers.mapError(err);
      triggerNotification({
        type: "error",
        title: isCreate ? "Create failed" : "Update failed",
        message: mapped.message,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      title={title}
      onClose={onClose}
      width={440}
      destroyOnClose
      maskClosable={false}
      extra={
        <Space>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={saving}
            onClick={handleSave}
            disabled={!isCreate && !formDirty}
          >
            Save
          </Button>
        </Space>
      }
    >
      {loading ? (
        <div style={{ textAlign: "center", padding: 48 }}>
          <Spin />
        </div>
      ) : (
        <Form form={form} layout="vertical" onValuesChange={checkDirty}>
          <Form.Item
            name="tag"
            label="Tag"
            rules={[
              { required: true, message: "Tag is required" },
              {
                validator: (_, value) =>
                  value && value.trim().length > 0
                    ? Promise.resolve()
                    : Promise.reject(new Error("Tag must not be empty")),
              },
              { max: 255, message: "Tag must be 255 characters or fewer" },
              { pattern: /^[a-zA-Z0-9\s_\-.,!?'"''""&()/:;]+$/, message: "Special characters like @#%^*+=<>{}[]\\|~` are not allowed" },
              { pattern: /[a-zA-Z]/, message: "Must contain at least one letter. Only numbers not allowed." },
            ]}
          >
            <Input
              placeholder="e.g. dryness"
              autoFocus={isCreate}
              allowClear
            />
          </Form.Item>
        </Form>
      )}
    </Drawer>
  );
};

export default TagFormDrawer;
